import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A utility hook that manages the trigger element state for popover components.
 *
 * This hook wraps useState and adds a development-mode safety check to ensure
 * the trigger element properly forwards refs.
 */
const useTriggerElement = () => {
  const [triggerElement, originalSetTriggerElement] =
    useState<HTMLElement | null>(null);
  const triggerElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;

    if (!triggerElementRef.current) {
      throw new Error(
        `useTriggerElement: Trigger element not found. This could be because:
          1. The trigger element is a React component that doesn't forward refs
          2. The trigger element is conditionally rendered
          
          Solution: If the trigger element is conditionally rendered, wrap the entire component in a conditional instead.`,
      );
    }
  }, []);

  const setTriggerElement = useCallback(
    (element: HTMLElement | null) => {
      triggerElementRef.current = element;
      originalSetTriggerElement(element);
    },
    [originalSetTriggerElement],
  );

  return [triggerElement, setTriggerElement] as const;
};

export default useTriggerElement;
